<script setup lang="ts">
interface Props {
  title: string;
  isOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: true,
});

const isExpanded = ref(props.isOpen);
</script>

<template>
  <div class="ui-collapsible">
    <div class="collapsible-header">
      <div class="header-left" @click="isExpanded = !isExpanded">
        <UiIcon
          class="icon-size-sm"
          :name="isExpanded ? 'ChevronDown' : 'ChevronRight'"
        />
        <h3 class="collapsible-title">{{ title }}</h3>
      </div>
      <div v-if="$slots.actions" class="actions">
        <slot name="actions" />
      </div>
    </div>
    <Transition name="collapse">
      <div v-if="isExpanded" class="collapsible-content">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.ui-collapsible {
  gap: var(--space-4);

  .collapsible-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: var(--border-radius-8);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    cursor: pointer;
    flex: 1;
    overflow: hidden;
  }

  .collapsible-title {
    font-size: var(--font-size-14);
    font-weight: var(--font-weight-bold);
    color: var(--color-gray-700);
    margin: 0;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    display: flex;
    align-items: center;
  }

  .collapsible-content {
    padding: 0 var(--space-8);
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
  }

  .collapse-enter-active,
  .collapse-leave-active {
    transition: all var(--duration-slow) var(--ease-standard);
    overflow: hidden;
  }

  .collapse-enter-from,
  .collapse-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  .collapse-enter-to,
  .collapse-leave-from {
    opacity: 1;
    max-height: 1000px;
  }
}
</style>
